import * as React from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { can, type Permission, type SessionUser } from '@tekvoice/shared';
import { api, ApiError } from '@/lib/api';

interface AuthContextValue {
  user: SessionUser | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<SessionUser>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
  can: (permission: Permission) => boolean;
}

const AuthContext = React.createContext<AuthContextValue | null>(null);

export const AUTH_QUERY_KEY = ['auth', 'me'] as const;

export function AuthProvider({ children }: { children: React.ReactNode }): React.JSX.Element {
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: AUTH_QUERY_KEY,
    queryFn: async () => {
      try {
        const response = await api.auth.me();
        return response.user;
      } catch (error) {
        // 401 simply means "not signed in" — it is not an error state for the UI.
        if (error instanceof ApiError && error.isUnauthenticated) return null;
        throw error;
      }
    },
    retry: false,
    staleTime: 60_000,
  });

  const user = data ?? null;

  const value = React.useMemo<AuthContextValue>(
    () => ({
      user,
      isLoading,
      isAuthenticated: user !== null,
      login: async (email, password) => {
        const response = await api.auth.login({ email, password });
        queryClient.setQueryData(AUTH_QUERY_KEY, response.user);
        return response.user;
      },
      logout: async () => {
        await api.auth.logout();
        queryClient.setQueryData(AUTH_QUERY_KEY, null);
        await queryClient.invalidateQueries();
      },
      refresh: async () => {
        await queryClient.invalidateQueries({ queryKey: AUTH_QUERY_KEY });
      },
      can: (permission) => can(user?.role ?? null, permission),
    }),
    [user, isLoading, queryClient],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = React.useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used inside <AuthProvider>');
  return context;
}
